import * as React from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Keyboard } from 'swiper';
import Container from '../Container';
import { QuotationSlide } from './quotation-box';

// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';

export default function QuotationSlider({ blok }) {
  const quotes = React.useMemo(
    () => (Array.isArray(blok?.quotes) ? blok.quotes : []),
    [blok?.quotes],
  );
  const rootRef = React.useRef(null);
  const [titleHeight, setTitleHeight] = React.useState(0);

  // Titles can wrap on narrow screens, so measure the tallest one and
  // reserve that much space on every slide to keep the cards aligned.
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const measure = () => {
      const heights = Array.from(root.querySelectorAll('[data-quotation-title]'))
        .map((el) => el.offsetHeight);
      setTitleHeight(Math.max(0, ...heights));
    };

    measure();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(measure)
      : null;
    root.querySelectorAll('[data-quotation-title]').forEach((el) => observer?.observe(el));
    window.addEventListener('resize', measure);
    if (document.fonts?.ready) {
      document.fonts.ready.then(measure);
    }
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [quotes]);

  if (quotes.length === 0) {
    return null;
  }

  return (
    <section
      ref={rootRef}
      {...storyblokEditable(blok)}
      className="py-12 lg:py-20"
    >
      <Container>
        {quotes.length === 1 ? (
          <QuotationSlide blok={quotes[0]} />
        ) : (
          <Swiper
            modules={[Pagination, Keyboard]}
            className="quotation-slider"
            slidesPerView={1}
            spaceBetween={24}
            keyboard={{ enabled: true }}
            pagination={{
              clickable: true,
              renderBullet(index, className) {
                return `<button
                  class="${className}"
                  aria-label="Quote ${index + 1}"
                  type="button"
                >
                  ${index + 1}
                </button>`;
              },
            }}
          >
            {quotes.map((quote) => (
              <SwiperSlide key={quote._uid} className="!h-auto">
                <QuotationSlide
                  blok={quote}
                  fillHeight
                  titleHeight={titleHeight}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </Container>
    </section>
  );
}
